import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Monitor, Smartphone, ArrowLeft, Check, ExternalLink, 
  Palette, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { TEMPLATES, TEMPLATE_INFO, getTemplateComponent, getTemplateInfo } from '../components/templates';

const API = process.env.REACT_APP_BACKEND_URL;

const EUPreview = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const templateId = searchParams.get('template') || 'madrid';
  const initialMode = searchParams.get('mode') || 'mobile';
  
  const [viewMode, setViewMode] = useState(initialMode);
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadBusiness();
  }, [slug, templateId]);

  const loadBusiness = async () => {
    try {
      // Primeiro tenta carregar preview com template específico
      const response = await fetch(`${API}/api/eu/demo/preview/${slug}/${templateId}`);
      
      if (response.ok) {
        const data = await response.json();
        setBusiness(data);
      } else {
        // Se não funcionar, tenta criar demo e carregar
        if (slug === 'carlos-electricidad') {
          await fetch(`${API}/api/eu/demo/carlos-electricidad`, { method: 'POST' });
          const retryResponse = await fetch(`${API}/api/eu/demo/preview/${slug}/${templateId}`);
          if (retryResponse.ok) {
            const data = await retryResponse.json();
            setBusiness(data);
          }
        }
      }
    } catch (error) {
      console.error('Error loading preview:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = async () => {
    setSaving(true);
    try {
      // Atualiza o template do business
      const response = await fetch(`${API}/api/eu/businesses/${slug}/design`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ template_id: templateId })
      });
      
      if (response.ok) {
        // Navega para a página pública
        navigate(`/eu/${slug}`);
      }
    } catch (error) {
      console.error('Error selecting template:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenFullscreen = () => {
    window.open(`/eu/${slug}?template=${templateId}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const TemplateComponent = getTemplateComponent(templateId);
  const info = getTemplateInfo(templateId);

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Toolbar */}
      <div className="fixed top-0 left-0 right-0 z-[100] bg-slate-900/95 backdrop-blur-sm border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          {/* Left - Back button */}
          <button
            onClick={() => navigate(`/eu/templates?business=${slug}`)}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
            <span className="hidden sm:inline">Volver a plantillas</span>
          </button>

          {/* Center - Template info & View toggle */}
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-2">
              <Palette size={18} style={{ color: info.primaryColor }} />
              <span className="font-medium text-white">{info.name}</span>
              <span className="text-slate-500 text-sm">- {info.description}</span>
            </div>

            {/* Device toggle */}
            <div className="flex items-center gap-1 bg-slate-800 rounded-lg p-1">
              <button
                onClick={() => setViewMode('mobile')}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                  viewMode === 'mobile'
                    ? 'bg-blue-600 text-white'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <Smartphone size={16} />
                <span className="hidden sm:inline">Móvil</span>
              </button>
              <button
                onClick={() => setViewMode('desktop')}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                  viewMode === 'desktop'
                    ? 'bg-blue-600 text-white'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <Monitor size={16} />
                <span className="hidden sm:inline">Escritorio</span>
              </button>
            </div>
          </div>

          {/* Right - Actions */}
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleOpenFullscreen}
              className="border-slate-700 text-slate-300 hover:bg-slate-800 hidden sm:flex"
            >
              <ExternalLink size={16} className="mr-2" />
              Abrir
            </Button>
            <Button
              onClick={handleSelectTemplate}
              disabled={saving}
              size="sm"
              className="bg-green-600 hover:bg-green-700"
            >
              {saving ? (
                <RefreshCw size={16} className="mr-2 animate-spin" />
              ) : (
                <Check size={16} className="mr-2" />
              )}
              Usar este diseño
            </Button>
          </div>
        </div>
      </div>

      {/* Preview Area */}
      <div className="pt-16 min-h-screen flex items-center justify-center p-4 sm:p-8">
        {viewMode === 'mobile' ? (
          /* Mobile Preview Frame - Using iframe for accurate rendering */
          <div className="relative">
            {/* Phone frame */}
            <div className="relative bg-slate-800 rounded-[3rem] p-3 shadow-2xl">
              {/* Notch */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-7 bg-slate-800 rounded-b-2xl z-10" />
              
              {/* Screen - Using iframe for accurate mobile rendering */}
              <div className="w-[375px] h-[812px] bg-white rounded-[2.5rem] overflow-hidden relative">
                <iframe
                  src={`/eu/${slug}?template=${templateId}`}
                  title="Mobile Preview"
                  className="w-full h-full border-0"
                  style={{ 
                    transform: 'scale(1)',
                    transformOrigin: 'top left'
                  }}
                />
              </div>
              
              {/* Home indicator */}
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-32 h-1 bg-slate-600 rounded-full" />
            </div>
          </div>
        ) : (
          /* Desktop Preview Frame */
          <div className="w-full max-w-6xl">
            {/* Browser chrome */}
            <div className="bg-slate-800 rounded-t-xl px-4 py-3 flex items-center gap-3">
              {/* Traffic lights */}
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                <div className="w-3 h-3 rounded-full bg-green-500" />
              </div>
              {/* URL bar */}
              <div className="flex-1 max-w-xl mx-auto">
                <div className="bg-slate-700 rounded-lg px-4 py-2 text-sm text-slate-400 text-center">
                  tudominio.es/{slug}
                </div>
              </div>
            </div>
            
            {/* Browser content */}
            <div className="bg-white rounded-b-xl overflow-hidden shadow-2xl">
              <div className="h-[600px] overflow-y-auto">
                <TemplateComponent business={business} isMobile={false} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Template switcher (floating) */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100]">
        <div className="bg-slate-800/95 backdrop-blur-sm rounded-2xl px-4 py-3 flex items-center gap-4 border border-slate-700 shadow-xl">
          <span className="text-slate-400 text-sm">Probar otro:</span>
          <div className="flex items-center gap-2">
            {Object.keys(TEMPLATES).map((tid) => {
              const tinfo = getTemplateInfo(tid);
              const isActive = tid === templateId;
              return (
                <button
                  key={tid}
                  onClick={() => navigate(`/eu/preview/${slug}?template=${tid}&mode=${viewMode}`)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'text-white'
                      : 'text-slate-400 hover:text-white hover:bg-slate-700'
                  }`}
                  style={isActive ? { backgroundColor: tinfo.primaryColor } : {}}
                >
                  {tinfo.name}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EUPreview;
